import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  HOCs,
  Menu,
  MenuList,
  MenuListItem,
  MaterialIcon,
} from '@pearsontechnology/aero-components';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import Modal, { ModalContent, ModalActions } from 'components/Modal';

import { PermissionTypes } from 'constants/enums';
import workspaceMessages from 'Workspace/messages';

import messages from './messages';
import {
  FormField,
  SelectLabel,
  ReadOnlyInput,
  WorkspaceDropdownWrapper,
  WorkspaceSelectTrigger,
  WorkspaceOptionsPanel,
  WorkspaceOptionItem,
} from './styles';

const { enhanceWithPopper } = HOCs;

const ROLE_OPTIONS = [
  { text: 'Owner', permission: PermissionTypes.OWNER },
  { text: 'Contributor', permission: PermissionTypes.CONTRIBUTOR },
  { text: 'Reviewer', permission: PermissionTypes.REVIEWER },
];

const RoleDropdownTrigger = ({ text, onClick, disabled }) => (
  <Button grey onClick={disabled ? undefined : onClick} disabled={disabled}>
    {text} <MaterialIcon id="arrow_drop_down" />
  </Button>
);

const RoleDropdownButton = enhanceWithPopper(RoleDropdownTrigger);

function getWorkspaceOptions(workspacesList) {
  if (!workspacesList) return [];
  const list = typeof workspacesList.toJS === 'function' ? workspacesList.toJS() : workspacesList;
  if (!Array.isArray(list)) return [];
  return list.map((w) => ({ value: w.id, label: w.name }));
}

function getDefaultSelectedIds(workspaceOptions, request) {
  const requestedName = request?.workspace?.trim();
  if (!requestedName || !workspaceOptions.length) return [];
  const match = workspaceOptions.find((opt) => opt.label === requestedName);
  return match ? [match.value] : [];
}

function getWorkspaceTriggerText(selectedWorkspaceIds, workspaceOptions) {
  if (!selectedWorkspaceIds.length) {
    return intl.formatMessage(messages.typePlaceholder);
  }
  return workspaceOptions
    .filter((opt) => selectedWorkspaceIds.includes(opt.value))
    .map((opt) => opt.label)
    .join(', ');
}

function ApproveRequestAccessModal({ visible, onDismiss, onFinalize, request, workspacesList }) {
  const workspaceOptions = getWorkspaceOptions(workspacesList);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState([]);
  const [role, setRole] = useState(PermissionTypes.CONTRIBUTOR);
  const [workspaceDropdownOpen, setWorkspaceDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      setWorkspaceDropdownOpen(false);
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;
    setRole(PermissionTypes.CONTRIBUTOR);
    setSelectedWorkspaceIds(getDefaultSelectedIds(getWorkspaceOptions(workspacesList), request));
  }, [visible, request, workspacesList]);

  useEffect(() => {
    if (!workspaceDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setWorkspaceDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [workspaceDropdownOpen]);

  const toggleWorkspace = (workspaceId) => {
    setSelectedWorkspaceIds((prev) => (
      prev.includes(workspaceId)
        ? prev.filter((id) => id !== workspaceId)
        : [...prev, workspaceId]
    ));
  };

  const workspaceTriggerText = getWorkspaceTriggerText(selectedWorkspaceIds, workspaceOptions);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      title={intl.formatMessage(messages.title)}
    >
      <ModalContent>
        <FormField>
          <SelectLabel>
            {intl.formatMessage(messages.requestedWorkspaceLabel)}
          </SelectLabel>
          <ReadOnlyInput
            id="approve-modal-requested-workspace"
            type="text"
            readOnly
            value={request?.workspace ?? ''}
          />
        </FormField>
        <FormField>
          <SelectLabel id="approve-modal-workspaces-label">
            {intl.formatMessage(messages.typeLabel)}
          </SelectLabel>
          <WorkspaceDropdownWrapper ref={dropdownRef}>
            <WorkspaceSelectTrigger
              type="button"
              disabled={!workspaceOptions.length}
              aria-haspopup="listbox"
              aria-expanded={workspaceDropdownOpen}
              aria-labelledby="approve-modal-workspaces-label"
              onClick={() => setWorkspaceDropdownOpen((open) => !open)}
            >
              <span>{workspaceTriggerText}</span>
              <MaterialIcon id="arrow_drop_down" />
            </WorkspaceSelectTrigger>
            {workspaceDropdownOpen && (
              <WorkspaceOptionsPanel id="approve-modal-workspaces" role="listbox" aria-multiselectable="true">
                {workspaceOptions.map((opt) => (
                  <WorkspaceOptionItem
                    key={opt.value}
                    htmlFor={`approve-modal-workspace-${opt.value}`}
                    selected={selectedWorkspaceIds.includes(opt.value)}
                  >
                    <Checkbox
                      id={`approve-modal-workspace-${opt.value}`}
                      checked={selectedWorkspaceIds.includes(opt.value)}
                      onChange={() => toggleWorkspace(opt.value)}
                    />
                    {opt.label}
                  </WorkspaceOptionItem>
                ))}
              </WorkspaceOptionsPanel>
            )}
          </WorkspaceDropdownWrapper>
        </FormField>
        <FormField>
          <SelectLabel id="approve-modal-role-label">
            {intl.formatMessage(messages.roleLabel)}
          </SelectLabel>
          <RoleDropdownButton
            placement="bottom-end"
            text={intl.formatMessage(workspaceMessages[`permission${ROLE_OPTIONS.find((r) => r.permission === role)?.text || 'Contributor'}`])}
            hidepopperonclick
            modifiers={{
              preventOverflow: { enabled: false },
              hide: { enabled: false },
              flip: { enabled: false },
            }}
            aria-labelledby="approve-modal-role-label"
          >
            <Menu width={130}>
              <MenuList>
                {ROLE_OPTIONS.map((item) => (
                  <MenuListItem
                    key={item.permission}
                    onClick={() => setRole(item.permission)}
                    selected={item.permission === role}
                  >
                    {intl.formatMessage(workspaceMessages[`permission${item.text}`])}
                  </MenuListItem>
                ))}
              </MenuList>
            </Menu>
          </RoleDropdownButton>
        </FormField>
      </ModalContent>
      <ModalActions>
        <Button grey onClick={onDismiss}>
          {intl.formatMessage(messages.cancel)}
        </Button>
        <Button
          primary
          disabled={!selectedWorkspaceIds.length}
          onClick={() => {
            onFinalize && onFinalize(request, selectedWorkspaceIds, role);
            onDismiss();
          }}
        >
          {intl.formatMessage(messages.finalize)}
        </Button>
      </ModalActions>
    </Modal>
  );
}

ApproveRequestAccessModal.propTypes = {
  visible: PropTypes.bool,
  onDismiss: PropTypes.func.isRequired,
  onFinalize: PropTypes.func,
  request: PropTypes.object,
  workspacesList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

ApproveRequestAccessModal.defaultProps = {
  visible: false,
  request: null,
  onFinalize: undefined,
  workspacesList: null,
};

export default ApproveRequestAccessModal;
